import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  DollarSign, Clock, Send, Ban, CheckCircle2, XCircle,
  CreditCard, Bitcoin, Filter, Edit, Trash2, Lock,
  TrendingUp, Users, AlertTriangle
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  en_attente: { label: 'En attente d\'envoi', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  envoye: { label: 'Envoyé', color: 'bg-blue-500/20 text-blue-400', icon: Send },
  bloque: { label: 'Bloqué', color: 'bg-red-500/20 text-red-400', icon: Ban },
  recu: { label: 'Reçu', color: 'bg-green-500/20 text-green-400', icon: CheckCircle2 },
  paye: { label: 'Payé', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle2 },
  annule: { label: 'Annulé', color: 'bg-gray-500/20 text-gray-400', icon: XCircle },
};

export default function AdminPayments() {
  const { token, user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [offices, setOffices] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [saving, setSaving] = useState(false);

  // Create form
  const [createForm, setCreateForm] = useState({
    analyst_id: '',
    client_id: '',
    date: new Date().toISOString().split('T')[0],
    amount: '',
    type: 'virement',
    rib_label: '',
    status: 'en_attente',
  });

  // Filters
  const now = new Date();
  const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const lastOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()}`;
  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(lastOfMonth);
  const [filterAnalyst, setFilterAnalyst] = useState('all');
  const [filterOffice, setFilterOffice] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchInitial();
  }, [token]);

  useEffect(() => {
    if (analysts.length >= 0) fetchPayments();
  }, [startDate, endDate, filterAnalyst, filterOffice]);

  const fetchInitial = async () => {
    try {
      const [analystsRes, officesRes, clientsRes] = await Promise.all([
        axios.get(`${API}/analysts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/offices`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setAnalysts(analystsRes.data || []);
      setOffices(Array.isArray(officesRes.data) ? officesRes.data : []);
      setAllClients(clientsRes.data || []);
    } catch (err) {
      console.error('Init error:', err);
    }
  };

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (filterAnalyst !== 'all') params.append('analyst_id', filterAnalyst);
      if (filterOffice !== 'all') params.append('office_id', filterOffice);

      const [paymentsRes, statsRes] = await Promise.all([
        axios.get(`${API}/payments?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/payments/stats?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setPayments(paymentsRes.data || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getAnalystName = (id) => {
    const a = analysts.find(x => x.id === id);
    return a ? `${a.first_name || ''} ${a.last_name || ''}`.trim() : id;
  };

  const getClientName = (clientId) => {
    const c = allClients.find(cl => cl.user_id === clientId || cl.id === clientId);
    if (c) {
      const u = c.user || c;
      return `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || clientId;
    }
    return clientId;
  };

  const getOfficeName = (analystId) => {
    const a = analysts.find(x => x.id === analystId);
    if (!a?.office_id) return '-';
    const o = offices.find(x => x.id === a.office_id);
    return o?.name || '-';
  };

  // Clients filtered by selected analyst in create form
  const createFormClients = allClients.filter(c => {
    if (!createForm.analyst_id) return false;
    return c.assigned_analyst_id === createForm.analyst_id;
  });

  const openEdit = (payment) => {
    setSelectedPayment({ ...payment });
    setEditDialogOpen(true);
  };

  const filteredPayments = filterStatus === 'all' ? payments : payments.filter(p => p.status === filterStatus);

  const handleCreate = async () => {
    if (!createForm.analyst_id || !createForm.client_id || !createForm.amount || !createForm.date) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/payments`, createForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Paiement créé avec succès');
      setCreateDialogOpen(false);
      setCreateForm({ analyst_id: '', client_id: '', date: new Date().toISOString().split('T')[0], amount: '', type: 'virement', rib_label: '', status: 'en_attente' });
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedPayment) return;
    setSaving(true);
    try {
      await axios.put(`${API}/payments/${selectedPayment.id}`, {
        status: selectedPayment.status,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Statut mis à jour');
      setEditDialogOpen(false);
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPayment) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/payments/${selectedPayment.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Paiement supprimé');
      setDeleteDialogOpen(false);
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-payments-page">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Paiements</h1>
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="admin-create-payment-btn">
            <DollarSign className="w-4 h-4 mr-2" />
            Nouveau Paiement
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="bg-card/80 backdrop-blur border-border/50">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p>
                <p className="text-2xl font-bold">{stats.total_payments}</p>
                <p className="text-sm text-muted-foreground">{stats.total_amount?.toLocaleString('fr-FR')} €</p>
              </CardContent>
            </Card>
            <Card className="bg-card/80 backdrop-blur border-border/50">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Virements</p>
                <p className="text-2xl font-bold">{stats.total_virement?.toLocaleString('fr-FR')} €</p>
              </CardContent>
            </Card>
            <Card className="bg-card/80 backdrop-blur border-border/50">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Crypto</p>
                <p className="text-2xl font-bold">{stats.total_crypto?.toLocaleString('fr-FR')} €</p>
              </CardContent>
            </Card>
            <Card className="bg-card/80 backdrop-blur border-border/50">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Commissions à venir</p>
                <p className="text-2xl font-bold text-yellow-400">{stats.commission_pending?.toLocaleString('fr-FR')} €</p>
              </CardContent>
            </Card>
            <Card className="bg-card/80 backdrop-blur border-border/50">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Commissions confirmées</p>
                <p className="text-2xl font-bold text-emerald-400">{stats.commission_confirmed?.toLocaleString('fr-FR')} €</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="bg-card/80 backdrop-blur border-border/50">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Du</Label>
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-44" data-testid="admin-filter-start" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Au</Label>
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-44" data-testid="admin-filter-end" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Analyste</Label>
                <Select value={filterAnalyst} onValueChange={setFilterAnalyst}>
                  <SelectTrigger className="w-48" data-testid="admin-filter-analyst">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les analystes</SelectItem>
                    {analysts.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {user?.role !== 'bureau' && offices.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">Bureau</Label>
                  <Select value={filterOffice} onValueChange={setFilterOffice}>
                    <SelectTrigger className="w-48" data-testid="admin-filter-office">
                      <SelectValue placeholder="Tous" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les bureaux</SelectItem>
                      {offices.map(o => (
                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs text-muted-foreground">Statut</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-48" data-testid="admin-filter-status">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="en_attente">En attente d'envoi</SelectItem>
                    <SelectItem value="envoye">Envoyé</SelectItem>
                    <SelectItem value="bloque">Bloqué</SelectItem>
                    <SelectItem value="recu">Reçu</SelectItem>
                    <SelectItem value="paye">Payé</SelectItem>
                    <SelectItem value="annule">Annulé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payments Table */}
        <Card className="bg-card/80 backdrop-blur border-border/50">
          <CardHeader>
            <CardTitle className="text-base uppercase tracking-wider">Liste des paiements</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12"><div className="spinner" /></div>
            ) : filteredPayments.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun paiement trouvé</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/50">
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Date</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Analyste</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Bureau</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Client</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Montant</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Type</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">RIB</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Statut</th>
                      <th className="text-right py-3 px-2 text-muted-foreground font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.map(p => {
                      const sc = STATUS_CONFIG[p.status] || {};
                      const Icon = sc.icon || Clock;
                      const isLocked = p.locked;
                      return (
                        <tr key={p.id} className="border-b border-border/20 hover:bg-secondary/20" data-testid={`admin-payment-row-${p.id}`}>
                          <td className="py-3 px-2">{new Date(p.date).toLocaleDateString('fr-FR')}</td>
                          <td className="py-3 px-2 font-medium">{getAnalystName(p.analyst_id)}</td>
                          <td className="py-3 px-2 text-muted-foreground">{getOfficeName(p.analyst_id)}</td>
                          <td className="py-3 px-2">{getClientName(p.client_id)}</td>
                          <td className="py-3 px-2 font-semibold">{p.amount?.toLocaleString('fr-FR')} €</td>
                          <td className="py-3 px-2">
                            <Badge variant="outline" className="text-xs">
                              {p.type === 'crypto' ? <><Bitcoin className="w-3 h-3 mr-1" />Crypto</> : <><CreditCard className="w-3 h-3 mr-1" />Virement</>}
                            </Badge>
                          </td>
                          <td className="py-3 px-2 text-muted-foreground max-w-[150px] truncate">{p.type === 'virement' ? (p.rib_label || '-') : '-'}</td>
                          <td className="py-3 px-2">
                            <Badge className={`${sc.color} text-xs`}>
                              <Icon className="w-3 h-3 mr-1" />
                              {sc.label}
                              {isLocked && <Lock className="w-3 h-3 ml-1" />}
                            </Badge>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`admin-edit-payment-${p.id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => { setSelectedPayment(p); setDeleteDialogOpen(true); }} data-testid={`admin-delete-payment-${p.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Status Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md" data-testid="admin-edit-payment-dialog">
            <DialogHeader>
              <DialogTitle>Modifier le statut</DialogTitle>
            </DialogHeader>
            {selectedPayment && (
              <div className="space-y-4">
                <div className="p-3 bg-secondary/30 rounded-lg text-sm space-y-1">
                  <p><span className="text-muted-foreground">Analyste :</span> {getAnalystName(selectedPayment.analyst_id)}</p>
                  <p><span className="text-muted-foreground">Client :</span> {getClientName(selectedPayment.client_id)}</p>
                  <p><span className="text-muted-foreground">Montant :</span> {selectedPayment.amount?.toLocaleString('fr-FR')} €</p>
                  <p><span className="text-muted-foreground">Type :</span> {selectedPayment.type === 'crypto' ? 'Crypto' : 'Virement'}</p>
                </div>
                <div>
                  <Label>Statut</Label>
                  <Select value={selectedPayment.status} onValueChange={v => setSelectedPayment(prev => ({ ...prev, status: v }))}>
                    <SelectTrigger data-testid="admin-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en_attente">En attente d'envoi</SelectItem>
                      <SelectItem value="envoye">Envoyé</SelectItem>
                      <SelectItem value="bloque">Bloqué</SelectItem>
                      <SelectItem value="recu">Reçu</SelectItem>
                      <SelectItem value="paye">Payé</SelectItem>
                      <SelectItem value="annule">Annulé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(selectedPayment.status === 'paye' || selectedPayment.status === 'annule') && (
                  <div className="flex items-center gap-2 p-2 bg-yellow-500/10 rounded-lg text-yellow-400 text-xs">
                    <AlertTriangle className="w-4 h-4" />
                    Ce statut verrouillera le paiement (l'analyste ne pourra plus le modifier)
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleUpdate} disabled={saving} data-testid="admin-update-status-btn">
                {saving ? 'Mise à jour...' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirm Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="max-w-sm" data-testid="delete-payment-dialog">
            <DialogHeader>
              <DialogTitle>Supprimer le paiement ?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Cette action est irréversible. Le paiement de {selectedPayment?.amount?.toLocaleString('fr-FR')} € sera définitivement supprimé.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
              <Button variant="destructive" onClick={handleDelete} disabled={saving} data-testid="confirm-delete-btn">
                {saving ? 'Suppression...' : 'Supprimer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Payment Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-md" data-testid="admin-create-payment-dialog">
            <DialogHeader>
              <DialogTitle>Nouveau Paiement</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Analyste *</Label>
                <Select value={createForm.analyst_id} onValueChange={v => setCreateForm(prev => ({ ...prev, analyst_id: v, client_id: '' }))}>
                  <SelectTrigger data-testid="admin-create-analyst-select">
                    <SelectValue placeholder="Sélectionner un analyste" />
                  </SelectTrigger>
                  <SelectContent>
                    {analysts.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Client *</Label>
                <Select value={createForm.client_id} onValueChange={v => setCreateForm(prev => ({ ...prev, client_id: v }))} disabled={!createForm.analyst_id}>
                  <SelectTrigger data-testid="admin-create-client-select">
                    <SelectValue placeholder={createForm.analyst_id ? "Sélectionner un client" : "Sélectionnez d'abord un analyste"} />
                  </SelectTrigger>
                  <SelectContent>
                    {createFormClients.map(c => {
                      const u = c.user || c;
                      return (
                        <SelectItem key={c.user_id || c.id} value={c.user_id || c.id}>
                          {u.first_name} {u.last_name}
                        </SelectItem>
                      );
                    })}
                    {createForm.analyst_id && createFormClients.length === 0 && (
                      <SelectItem value="_none" disabled>Aucun client assigné</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input type="date" value={createForm.date} onChange={e => setCreateForm(prev => ({ ...prev, date: e.target.value }))} data-testid="admin-create-date" />
              </div>
              <div>
                <Label>Montant (€) *</Label>
                <Input type="number" step="0.01" value={createForm.amount} onChange={e => setCreateForm(prev => ({ ...prev, amount: e.target.value }))} placeholder="0.00" data-testid="admin-create-amount" />
              </div>
              <div>
                <Label>Type *</Label>
                <Select value={createForm.type} onValueChange={v => setCreateForm(prev => ({ ...prev, type: v, rib_label: v === 'crypto' ? '' : prev.rib_label }))}>
                  <SelectTrigger data-testid="admin-create-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="virement">Virement</SelectItem>
                    <SelectItem value="crypto">Crypto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {createForm.type === 'virement' && (
                <div>
                  <Label>Intitulé du RIB</Label>
                  <Input value={createForm.rib_label} onChange={e => setCreateForm(prev => ({ ...prev, rib_label: e.target.value }))} placeholder="Ex: Banque Populaire FR76..." data-testid="admin-create-rib" />
                </div>
              )}
              <div>
                <Label>Statut</Label>
                <Select value={createForm.status} onValueChange={v => setCreateForm(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger data-testid="admin-create-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en_attente">En attente d'envoi</SelectItem>
                    <SelectItem value="envoye">Envoyé</SelectItem>
                    <SelectItem value="bloque">Bloqué</SelectItem>
                    <SelectItem value="recu">Reçu</SelectItem>
                    <SelectItem value="paye">Payé</SelectItem>
                    <SelectItem value="annule">Annulé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleCreate} disabled={saving} data-testid="admin-submit-payment-btn">
                {saving ? 'Création...' : 'Créer le paiement'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
